import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('ebd_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

const ConfigEmailTemplates = ({ setCurrentView }) => {
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('trial_welcome');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/email-templates`, { headers: getAuthHeaders() });
      setTemplates(response.data);
    } catch (error) {
      showMessage('error', 'Erro ao carregar templates: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/email-templates`, templates, { headers: getAuthHeaders() });
      showMessage('success', 'Templates salvos com sucesso!');
    } catch (error) {
      showMessage('error', 'Erro ao salvar: ' + (error.response?.data?.detail || error.message));
    }
    setSaving(false);
  };

  const updateField = (section, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [section]: typeof prev[section] === 'object' && !Array.isArray(prev[section])
        ? { ...prev[section], [field]: value }
        : value
    }));
  };

  const updateGlobal = (field, value) => {
    setTemplates(prev => ({ ...prev, [field]: value }));
  };

  const updateListItem = (section, field, index, value) => {
    setTemplates(prev => {
      const list = [...(prev[section]?.[field] || [])];
      list[index] = value;
      return { ...prev, [section]: { ...prev[section], [field]: list } };
    });
  };

  const addListItem = (section, field) => {
    setTemplates(prev => {
      const list = [...(prev[section]?.[field] || []), ''];
      return { ...prev, [section]: { ...prev[section], [field]: list } };
    });
  };

  const removeListItem = (section, field, index) => {
    setTemplates(prev => {
      const list = (prev[section]?.[field] || []).filter((_, i) => i !== index);
      return { ...prev, [section]: { ...prev[section], [field]: list } };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando templates...</p>
        </div>
      </div>
    );
  }

  if (!templates) return null;

  const tabs = [
    { key: 'trial_welcome', label: 'Boas-vindas', icon: '🎉', color: 'emerald' },
    { key: 'trial_expiring', label: 'Expirando', icon: '⏳', color: 'amber' },
    { key: 'trial_not_logged_24h', label: 'Não Logou 24h', icon: '📩', color: 'indigo' },
    { key: 'trial_not_logged_72h', label: 'Não Logou 72h', icon: '📩', color: 'purple' },
    { key: 'global', label: 'Geral', icon: '⚙️', color: 'gray' },
  ];

  const renderTextField = (section, field, label, placeholder, multiline = false) => {
    const value = templates[section]?.[field] || '';
    return (
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => updateField(section, field, e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            rows={3}
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => updateField(section, field, e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        )}
      </div>
    );
  };

  const renderListField = (section, field, label) => {
    const items = templates[section]?.[field] || [];
    return (
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={i} className="flex gap-2">
              <input
                type="text"
                value={item}
                onChange={(e) => updateListItem(section, field, i, e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <button
                onClick={() => removeListItem(section, field, i)}
                className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm"
                title="Remover"
              >
                X
              </button>
            </div>
          ))}
          <button
            onClick={() => addListItem(section, field)}
            className="px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium"
          >
            + Adicionar item
          </button>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'trial_welcome':
        return (
          <div>
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-700">Email enviado automaticamente quando alguem cadastra um trial.</p>
              <p className="text-xs text-emerald-600 mt-1">Variaveis disponiveis: {'{nome_igreja}'}</p>
            </div>
            {renderTextField('trial_welcome', 'titulo', 'Titulo do Email', 'Bem-vindo ao seu periodo de teste!')}
            {renderTextField('trial_welcome', 'mensagem', 'Mensagem Principal', 'Sua conta foi criada...', true)}
            {renderTextField('trial_welcome', 'periodo_texto', 'Texto do Periodo', 'Voce tem acesso completo...', true)}
            {renderListField('trial_welcome', 'como_comecar', 'Como Comecar (passos)')}
            {renderListField('trial_welcome', 'recursos', 'Principais Recursos')}
          </div>
        );
      case 'trial_expiring':
        return (
          <div>
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">Email enviado quando o trial esta prestes a expirar (15, 7, 3 e 1 dia antes).</p>
            </div>
            {renderTextField('trial_expiring', 'titulo', 'Titulo do Email', 'Seu periodo de teste esta acabando')}
            {renderTextField('trial_expiring', 'mensagem', 'Mensagem Principal', 'Gostou do App EBD?...', true)}
            {renderTextField('trial_expiring', 'botao_texto', 'Texto do Botao', 'Contratar pelo WhatsApp')}
          </div>
        );
      case 'trial_not_logged_24h':
        return (
          <div>
            <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-indigo-700">Email enviado ~24h apos cadastro se o usuario nunca logou.</p>
            </div>
            {renderTextField('trial_not_logged_24h', 'titulo', 'Titulo', 'Sua conta esta pronta!')}
            {renderTextField('trial_not_logged_24h', 'subtitulo', 'Subtitulo', 'Falta so voce entrar')}
            {renderTextField('trial_not_logged_24h', 'mensagem', 'Mensagem Principal', 'Voce criou sua conta...', true)}
            {renderListField('trial_not_logged_24h', 'itens', 'O que vai encontrar')}
            {renderTextField('trial_not_logged_24h', 'botao_texto', 'Texto do Botao', 'Acessar Agora')}
          </div>
        );
      case 'trial_not_logged_72h':
        return (
          <div>
            <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-700">Email enviado ~72h apos cadastro se o usuario nunca logou.</p>
              <p className="text-xs text-purple-600 mt-1">Variaveis disponiveis: {'{nome_igreja}'}</p>
            </div>
            {renderTextField('trial_not_logged_72h', 'titulo', 'Titulo', 'Sentimos sua falta!')}
            {renderTextField('trial_not_logged_72h', 'subtitulo', 'Subtitulo', 'Sua conta continua esperando')}
            {renderTextField('trial_not_logged_72h', 'mensagem', 'Mensagem Principal', 'Voce criou sua conta para...', true)}
            {renderListField('trial_not_logged_72h', 'itens', 'O que vai encontrar')}
            {renderTextField('trial_not_logged_72h', 'botao_texto', 'Texto do Botao', 'Acessar Agora')}
          </div>
        );
      case 'global':
        return (
          <div>
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-700">Configuracoes usadas em todos os templates de email.</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">WhatsApp Suporte</label>
              <input
                type="text"
                value={templates.whatsapp_suporte || ''}
                onChange={(e) => updateGlobal('whatsapp_suporte', e.target.value)}
                placeholder="(19) 99791-0336"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Email Suporte</label>
              <input
                type="text"
                value={templates.email_suporte || ''}
                onChange={(e) => updateGlobal('email_suporte', e.target.value)}
                placeholder="marciosfbrk@gmail.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Link do Sistema</label>
              <input
                type="text"
                value={templates.link_sistema || ''}
                onChange={(e) => updateGlobal('link_sistema', e.target.value)}
                placeholder="https://ebdad.com.br"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Link do Tutorial (PDF)</label>
              <input
                type="text"
                value={templates.link_tutorial || ''}
                onChange={(e) => updateGlobal('link_tutorial', e.target.value)}
                placeholder="https://ebdad.com.br/Tutorial_EBD_Multi.pdf"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 via-indigo-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Configuracao de Emails</h1>
              <p className="text-indigo-200 text-sm mt-1">Edite os textos dos emails automaticos do sistema</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 bg-white text-indigo-700 rounded-xl hover:bg-indigo-50 font-semibold shadow-md transition-all text-sm disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
              <button
                onClick={() => setCurrentView('gerenciar-igrejas')}
                className="px-4 py-2.5 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 transition-all text-sm font-medium"
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Mensagem */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl shadow-sm border ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                activeTab === tab.key
                  ? `bg-${tab.color}-600 text-white shadow-md`
                  : `bg-white text-gray-600 border border-gray-200 hover:bg-gray-50`
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          {renderTabContent()}
        </div>

        {/* Botao Salvar (rodape) */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 font-semibold shadow-lg transition-all disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Salvar Alteracoes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigEmailTemplates;
